import CoursesBookmarkIcon from "@/assets/images/icons/courses_bookmark_icon.svg";
import FolderIcon from "@/assets/images/icons/folder.svg";
import PlayCircleIcon from "@/assets/images/icons/play-circle.svg";
import ExternalLink from "@/components/ExternalLink";
import {
  CaptionText,
  CardTitle,
} from "@/components/ui/Typography";
import { BRAND_COLORS } from "@/lib/brandColors";
import {
  UserBookmark,
  UserFolder,
} from "@/supabase/services/userFolders";
import { Ionicons } from "@expo/vector-icons";
import React, {
  memo,
  useCallback,
  useMemo,
  type MutableRefObject,
  type RefObject,
} from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";
import {
  DraxList,
  DraxSnapbackTargetPreset,
  DraxView,
  type SortableReorderEvent,
} from "react-native-drax";
import { XStack, YStack } from "tamagui";
import {
  extractBookmark,
  getBookmarkUrl,
  getDisplayTitle,
  hapticLight,
  isBookmarkInFolder,
  isCourseBookmark,
  isFolderLocked,
  receiverInsertIndex,
} from "./bookmarksHelpers";

export type FolderSectionHandlers = {
  toggleExpanded: (folderId: number) => void;
  openRename: (folder: UserFolder) => void;
  openDelete: (folder: UserFolder) => void;
  openMove: (
    bookmark: UserBookmark,
    title: string,
    fromFolderId: number,
  ) => void;
  runRemoveBookmark: (bookmark: UserBookmark) => void;
  markFolderHover: (folderId: number) => void;
  markCrossDropTarget: (
    folderId: number,
    beforeItemId: number | null,
  ) => void;
  scheduleFolderHoverExit: (folderId: number) => void;
  applyCrossFolderMove: (
    bookmarkId: number,
    targetFolderId: number,
    toIndex?: number,
    bookmarkHint?: UserBookmark,
  ) => void;
  handleItemReorder: (
    folderId: number,
    event: SortableReorderEvent<UserBookmark>,
  ) => void;
  clearDragUi: () => void;
};

type BookmarkRowProps = {
  bm: UserBookmark;
  folderId: number;
  index: number;
  isLast: boolean;
  showCrossDropSlot: boolean;
  busy: boolean;
  handlers: FolderSectionHandlers;
};

const BookmarkRow = memo(function BookmarkRow({
  bm,
  folderId,
  index,
  isLast,
  showCrossDropSlot,
  busy,
  handlers,
}: BookmarkRowProps) {
  const course = isCourseBookmark(bm);
  const title = getDisplayTitle(bm);

  return (
    <View
      style={[
        styles.lessonHit,
        index === 0 ? styles.lessonHitFirst : styles.lessonHitSpaced,
        isLast ? styles.lessonHitFooter : null,
      ]}
    >
      {showCrossDropSlot ? <View style={styles.crossDropSlot} /> : null}
      <View style={styles.lessonCard}>
        <Ionicons
          name="menu"
          size={18}
          color={BRAND_COLORS.muted}
          style={{ marginRight: 10 }}
        />
        <YStack
          w={34}
          h={34}
          br={9}
          ai="center"
          jc="center"
          bg="#F4F1ED"
          flexShrink={0}
          mr={10}
        >
          {course ? (
            <CoursesBookmarkIcon width={15} height={15} />
          ) : (
            <PlayCircleIcon
              width={15}
              height={15}
              color={BRAND_COLORS.text}
            />
          )}
        </YStack>
        <YStack flex={1} minWidth={0} jc="center">
          <View style={{ alignSelf: "flex-start", maxWidth: "100%" }}>
            <ExternalLink url={getBookmarkUrl(bm)} deferNavigation>
              <CardTitle
                fontWeight="600"
                color={BRAND_COLORS.text}
                numberOfLines={2}
                {...(Platform.OS === "web"
                  ? ({
                      cursor: "pointer",
                      hoverStyle: {
                        color: BRAND_COLORS.linkAccent,
                      },
                    } as object)
                  : null)}
              >
                {title}
              </CardTitle>
            </ExternalLink>
          </View>
        </YStack>
        {!busy ? (
          <XStack
            gap={14}
            flexShrink={0}
            ai="center"
            pl="$2"
            onStartShouldSetResponder={() => true}
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Pressable
              onPress={(e) => {
                e?.stopPropagation?.();
                handlers.openMove(bm, title, folderId);
              }}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Move bookmark"
            >
              <Ionicons
                name="swap-horizontal-outline"
                size={18}
                color={BRAND_COLORS.muted}
              />
            </Pressable>
            <Pressable
              onPress={(e) => {
                e?.stopPropagation?.();
                handlers.runRemoveBookmark(bm);
              }}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Remove bookmark"
            >
              <Ionicons
                name="trash-outline"
                size={18}
                color={BRAND_COLORS.muted}
              />
            </Pressable>
          </XStack>
        ) : null}
      </View>
    </View>
  );
});

type FolderSectionProps = {
  folder: UserFolder;
  folderId: number;
  items: UserBookmark[];
  isOpen: boolean;
  isDropTarget: boolean;
  /** undefined = no cross preview; null = append; number = insert before id */
  crossInsertBeforeId: number | null | undefined;
  listEpoch: number;
  busy: boolean;
  handlers: FolderSectionHandlers;
  folderBookmarksRef: MutableRefObject<Record<number, UserBookmark[]>>;
  skipFolderReorderRef: RefObject<Set<number>>;
  applyWithinFolderReorderRef: MutableRefObject<
    (folderId: number, next: UserBookmark[]) => void
  >;
};

export const BookmarksFolderSection = memo(function BookmarksFolderSection({
  folder,
  folderId,
  items,
  isOpen,
  isDropTarget,
  crossInsertBeforeId,
  listEpoch,
  busy,
  handlers,
  folderBookmarksRef,
  skipFolderReorderRef,
  applyWithinFolderReorderRef,
}: FolderSectionProps) {
  const listData = isOpen ? items : [];
  const locked = isFolderLocked(folder);

  const itemDraxViewProps = useMemo(
    () => ({
      draggable: !busy,
      hoverStyle: styles.hoverStyle,
      draggingStyle: styles.draggingStyle,
      onReceiveDragEnter: (eventData: {
        dragged: { payload: unknown };
        receiver?: { payload: unknown };
      }) => {
        const bookmark = extractBookmark(eventData.dragged.payload);
        if (!bookmark) return;
        if (
          isBookmarkInFolder(
            folderBookmarksRef.current[folderId],
            bookmark.id,
          )
        ) {
          return;
        }
        handlers.markFolderHover(folderId);
        const receiver = extractBookmark(eventData.receiver?.payload);
        if (receiver?.id != null) {
          handlers.markCrossDropTarget(folderId, Number(receiver.id));
        }
      },
      onReceiveDragOver: (eventData: {
        dragged: { payload: unknown };
        receiver?: { payload: unknown };
      }) => {
        const bookmark = extractBookmark(eventData.dragged.payload);
        if (!bookmark) return;
        if (
          isBookmarkInFolder(
            folderBookmarksRef.current[folderId],
            bookmark.id,
          )
        ) {
          return;
        }
        handlers.markFolderHover(folderId);
        const receiver = extractBookmark(eventData.receiver?.payload);
        if (receiver?.id != null) {
          handlers.markCrossDropTarget(folderId, Number(receiver.id));
        }
      },
      onReceiveDragExit: () => handlers.scheduleFolderHoverExit(folderId),
      onReceiveDragDrop: (eventData: {
        dragged: { payload: unknown };
        receiver?: { payload: unknown };
      }) => {
        const bookmark = extractBookmark(eventData.dragged.payload);
        if (!bookmark) {
          return DraxSnapbackTargetPreset.None;
        }

        const list = folderBookmarksRef.current[folderId] ?? [];
        const fromIndex = list.findIndex(
          (b) => Number(b.id) === Number(bookmark.id),
        );

        if (fromIndex >= 0) {
          const receiverItem = extractBookmark(eventData.receiver?.payload);
          let toIndex = receiverItem
            ? list.findIndex(
                (b) => Number(b.id) === Number(receiverItem.id),
              )
            : -1;
          if (toIndex < 0) {
            toIndex =
              receiverInsertIndex(eventData.receiver?.payload) ?? -1;
          }
          if (toIndex >= 0 && fromIndex !== toIndex) {
            const next = [...list];
            const [removed] = next.splice(fromIndex, 1);
            if (removed) {
              next.splice(toIndex, 0, removed);
              skipFolderReorderRef.current?.add(folderId);
              setTimeout(() => {
                skipFolderReorderRef.current?.delete(folderId);
              }, 400);
              applyWithinFolderReorderRef.current(folderId, next);
            }
          }
          return DraxSnapbackTargetPreset.None;
        }

        handlers.applyCrossFolderMove(
          bookmark.id,
          folderId,
          receiverInsertIndex(eventData.receiver?.payload),
          bookmark,
        );
        return DraxSnapbackTargetPreset.None;
      },
    }),
    // handlers object is stable from parent; refs are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs + stable handlers bag
    [busy, folderId, handlers],
  );

  const containerDraxViewProps = useMemo(
    () => ({
      // Critical: receive handlers make the list container steal drops
      // (folder highlight “glitch”) and block reorder.
      receptive: false as const,
    }),
    [],
  );

  const listLen = listData.length;
  const renderItem = useCallback(
    ({ item: bm, index }: { item: UserBookmark; index: number }) => (
      <BookmarkRow
        bm={bm}
        folderId={folderId}
        index={index}
        isLast={index === listLen - 1}
        showCrossDropSlot={
          crossInsertBeforeId != null &&
          Number(crossInsertBeforeId) === Number(bm.id)
        }
        busy={busy}
        handlers={handlers}
      />
    ),
    [busy, crossInsertBeforeId, folderId, handlers, listLen],
  );

  const onDragStart = useCallback(() => {
    hapticLight();
  }, []);

  const onDragEnd = useCallback(() => {
    handlers.clearDragUi();
  }, [handlers]);

  const onReorder = useCallback(
    (event: SortableReorderEvent<UserBookmark>) => {
      handlers.handleItemReorder(folderId, event);
    },
    [folderId, handlers],
  );

  const keyExtractor = useCallback((item: UserBookmark) => String(item.id), []);

  const listHeader = (
    <DraxView
      receptive
      onReceiveDragEnter={() => {
        handlers.markFolderHover(folderId);
        handlers.markCrossDropTarget(folderId, null);
      }}
      onReceiveDragOver={() => {
        handlers.markFolderHover(folderId);
        handlers.markCrossDropTarget(folderId, null);
      }}
      onReceiveDragExit={() => handlers.scheduleFolderHoverExit(folderId)}
      onReceiveDragDrop={(eventData) => {
        const bookmark = extractBookmark(eventData.dragged.payload);
        if (!bookmark) {
          return DraxSnapbackTargetPreset.None;
        }
        if (
          isBookmarkInFolder(
            folderBookmarksRef.current[folderId],
            bookmark.id,
          )
        ) {
          return DraxSnapbackTargetPreset.None;
        }
        handlers.applyCrossFolderMove(
          bookmark.id,
          folderId,
          undefined,
          bookmark,
        );
        return DraxSnapbackTargetPreset.None;
      }}
    >
      <XStack
        ai="center"
        px="$3.5"
        py="$3"
        gap="$2.5"
        width="100%"
        bg={BRAND_COLORS.cardBg}
        borderBottomWidth={isOpen ? StyleSheet.hairlineWidth : 0}
        borderBottomColor="rgba(0,0,0,0.06)"
      >
        <Pressable
          onPress={() => handlers.toggleExpanded(folderId)}
          style={{ flex: 1, minWidth: 0 }}
        >
          <XStack ai="center" gap="$3" flex={1} minWidth={0}>
            <YStack
              w={38}
              h={38}
              br={11}
              ai="center"
              jc="center"
              bg="white"
              borderWidth={1}
              borderColor="rgba(0,0,0,0.06)"
              flexShrink={0}
            >
              <FolderIcon
                width={16}
                height={16}
                color={BRAND_COLORS.muted}
              />
            </YStack>
            <YStack flex={1} minWidth={0} gap={2}>
              <CardTitle
                fontWeight="600"
                color={BRAND_COLORS.text}
                numberOfLines={1}
              >
                {folder.title}
              </CardTitle>
              <CaptionText
                color={
                  isDropTarget
                    ? BRAND_COLORS.linkAccent
                    : BRAND_COLORS.muted
                }
                {...(isDropTarget ? { fontWeight: "600" as const } : null)}
              >
                {isDropTarget
                  ? "Drop to move here"
                  : `${items.length} item${items.length === 1 ? "" : "s"}`}
              </CaptionText>
            </YStack>
          </XStack>
        </Pressable>

        {!locked ? (
          <XStack gap={14} flexShrink={0} ai="center" mr={4}>
            <Pressable
              onPress={() => handlers.openRename(folder)}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Rename folder"
            >
              <Ionicons
                name="pencil-outline"
                size={18}
                color={BRAND_COLORS.muted}
              />
            </Pressable>
            <Pressable
              onPress={() => handlers.openDelete(folder)}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Delete folder"
            >
              <Ionicons
                name="trash-outline"
                size={18}
                color={BRAND_COLORS.muted}
              />
            </Pressable>
          </XStack>
        ) : null}

        <Pressable
          onPress={() => handlers.toggleExpanded(folderId)}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={isOpen ? "Collapse folder" : "Expand folder"}
        >
          <Ionicons
            name={isOpen ? "chevron-up" : "chevron-down"}
            size={18}
            color={BRAND_COLORS.muted}
          />
        </Pressable>
      </XStack>
    </DraxView>
  );

  const listEmpty = !isOpen ? null : (
    <DraxView
      receptive
      onReceiveDragEnter={() => {
        handlers.markFolderHover(folderId);
        handlers.markCrossDropTarget(folderId, null);
      }}
      onReceiveDragOver={() => {
        handlers.markFolderHover(folderId);
        handlers.markCrossDropTarget(folderId, null);
      }}
      onReceiveDragExit={() => handlers.scheduleFolderHoverExit(folderId)}
      onReceiveDragDrop={(eventData) => {
        const bookmark = extractBookmark(eventData.dragged.payload);
        if (!bookmark) {
          return DraxSnapbackTargetPreset.None;
        }
        if (
          isBookmarkInFolder(
            folderBookmarksRef.current[folderId],
            bookmark.id,
          )
        ) {
          return DraxSnapbackTargetPreset.None;
        }
        handlers.applyCrossFolderMove(
          bookmark.id,
          folderId,
          undefined,
          bookmark,
        );
        return DraxSnapbackTargetPreset.None;
      }}
    >
      <View
        style={[
          styles.emptyDrop,
          isDropTarget ? styles.emptyDropActive : null,
        ]}
      >
        <CaptionText
          color={
            isDropTarget ? BRAND_COLORS.linkAccent : BRAND_COLORS.muted
          }
          textAlign="center"
          {...(isDropTarget ? { fontWeight: "600" as const } : null)}
        >
          {isDropTarget ? "Drop to move here" : "Drop bookmarks here"}
        </CaptionText>
      </View>
    </DraxView>
  );

  return (
    <View
      style={[styles.folderOuter, isDropTarget ? styles.folderActive : null]}
    >
      <DraxList<UserBookmark>
        key={`folder-${folderId}-e${listEpoch}`}
        id={`folder-bookmarks-${folderId}`}
        data={listData}
        keyExtractor={keyExtractor}
        scrollEnabled={false}
        longPressDelay={220}
        animationConfig="none"
        style={styles.folderCard}
        containerStyle={styles.folderCard}
        containerDraxViewProps={containerDraxViewProps}
        ListHeaderComponent={listHeader}
        ListFooterComponent={null}
        ListEmptyComponent={listEmpty}
        renderItem={renderItem}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onReorder={onReorder}
        itemDraxViewProps={itemDraxViewProps}
        contentContainerStyle={undefined}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  folderOuter: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.08)",
    backgroundColor: BRAND_COLORS.cardBg,
    overflow: "hidden",
  },
  folderActive: {
    borderColor: BRAND_COLORS.linkAccent,
    backgroundColor: "#FFFcf8",
  },
  folderCard: {
    backgroundColor: "transparent",
  },
  emptyDrop: {
    marginHorizontal: 12,
    marginBottom: 10,
    marginTop: 2,
    minHeight: 56,
    paddingVertical: 16,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: "rgba(0,0,0,0.12)",
    backgroundColor: "rgba(244,241,237,0.55)",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyDropActive: {
    borderColor: BRAND_COLORS.linkAccent,
    backgroundColor: "rgba(121,92,64,0.08)",
  },
  lessonHit: {
    paddingHorizontal: 12,
  },
  lessonHitFirst: {
    paddingTop: 8,
  },
  lessonHitSpaced: {
    paddingTop: 8,
  },
  lessonHitFooter: {
    paddingBottom: 8,
  },
  crossDropSlot: {
    height: 58,
    marginBottom: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
    backgroundColor: "#FAF8F5",
    opacity: 0.45,
  },
  lessonCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FAF8F5",
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
    ...(Platform.OS === "web" ? ({ cursor: "grab" } as object) : null),
  },
  hoverStyle: {
    backgroundColor: "transparent",
  },
  draggingStyle: {
    opacity: 0.35,
  },
});
